// preview: the booking page's live preview of a creative, drawn by the same renderer the site uses, so
// the sponsor sees exactly what visitors will. Loaded only on sponsor.st. One global: SponsorPreview.

import { creative, mount } from './render.js';
import { themer } from './theme.js';
import { BOARD_CSS, drawBoard } from './board-draw.js';

// The top bar sits in the preview box instead of the screen.
const PREVIEW = `.sl-top{position:relative;inset:auto;z-index:auto}
${BOARD_CSS}`;

/** @type {CSSStyleSheet | undefined} */
let sheet;

/**
 * Draws the sponsor into `host`, replacing the last drawing. Errors never break the page.
 * @param {HTMLElement} host
 * @param {string} format
 * @param {import('./config.js').Sponsor} sponsor
 * @param {import('./config.js').Look} [look]  the site's "How sponsors look"; the colours come from the page around `host`
 */
function render(host, format, sponsor, look = {}) {
    paint(host, format, look, box => box.append(creative({ ...sponsor, brand: sponsor.brand || 'Your brand' }, format)));
}

/**
 * Draws a leaderboard into `host` as the site shows it: the bidder's own row already in its place.
 * @param {HTMLElement} host
 * @param {import('./config.js').Board} rows  the board, the bidder's row included
 * @param {import('./config.js').Look} [look]
 */
function board(host, rows, look = {}) {
    paint(host, '', look, box => drawBoard(box, rows));
}

/**
 * The same shadow root and wrapper a slot has on a site (render.js mount), the site's colours, and `fill`
 * drawing into it, replacing the last drawing. Errors never break the page.
 * @param {HTMLElement} host
 * @param {string} format  '' for a leaderboard
 * @param {import('./config.js').Look} look
 * @param {(box: HTMLElement) => void} fill
 */
function paint(host, format, look, fill) {
    try {
        if (!sheet) {
            sheet = new CSSStyleSheet();
            sheet.replaceSync(PREVIEW);
        }
        // the same colours s.js picks on the site; with no accent set, plain ink (not sponsor.st's links)
        const { dark, vars } = themer({ ...look, accent: look.accent || '#18181b' })(host);
        host.dataset.slTheme = dark ? 'dark' : 'light';
        for (const [name, value] of Object.entries(vars)) host.style.setProperty(name, value);
        const box = mount(host, format, [sheet]);
        box.replaceChildren();
        fill(box);
    } catch (e) {
        console.warn('Sponsor preview:', e);
    }
}

/** @type {any} */ (window).SponsorPreview = Object.assign(render, { board });
